/**
 * Machine tokens — the credential for a machine with nobody in front of it.
 *
 * Three honesties this page owes the person reading it. The secret is shown
 * exactly once, because the alternative — "show it to me again" — is the same
 * sentence as "we are holding your credential in plaintext". A token is *your*
 * credential and not a service account's: whoever holds it can do everything you
 * can do with the CLI, so the warning sits beside the secret and not in a docs
 * page. And revoking one stops the next cold start, not the container that is
 * already running on a lease — that one runs until its grace window closes, which
 * is the same bound every other revocation in this product has.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'

import type { MachineToken } from '@/entities/types'
import {
  useAccount,
  useCreateMachineToken,
  useMachineTokens,
  useRevokeMachineToken,
} from '@/shared/api/hooks'
import { capture } from '@/shared/lib/analytics'
import { AppShell, PageHead } from '@/shared/ui/Chrome'
import {
  Badge,
  Button,
  ConfirmDialog,
  Empty,
  ErrorNotice,
  Field,
  Fig,
  Loading,
  Notice,
  Panel,
  Readout,
  Select,
  formatDate,
  formatDateTime,
  formatRelative,
} from '@/shared/ui/primitives'

/**
 * The lifespans the picker offers. A year is the server's ceiling; anything the
 * server clamps would be a picker lying about what it asked for, so nothing
 * longer is offered here.
 */
const EXPIRY_OPTIONS = [
  { value: '7', label: '7 days' },
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
  { value: '365', label: '1 year' },
] as const

const DEFAULT_EXPIRY = '30'

/** A token just minted: the one moment the secret exists on this side. */
interface Minted {
  id: string
  name: string
  token: string
  expiresAt: string
}

export function MachineTokens() {
  const { data: account } = useAccount()
  const { data: tokens, isPending, error } = useMachineTokens()
  const create = useCreateMachineToken()
  const revoke = useRevokeMachineToken()

  const [creating, setCreating] = useState(false)
  const [draftName, setDraftName] = useState('')
  const [draftExpiry, setDraftExpiry] = useState<string>(DEFAULT_EXPIRY)
  const [minted, setMinted] = useState<Minted | null>(null)
  const [confirming, setConfirming] = useState<MachineToken | null>(null)
  const [copied, setCopied] = useState(false)

  const live = tokens?.filter((token) => isLive(token)) ?? []
  const expiringSoon = live.filter((token) => daysUntil(token.expires_at) <= 7)
  const canCreate = account?.has_access ?? false

  function openCreate() {
    setDraftName('')
    setDraftExpiry(DEFAULT_EXPIRY)
    setCreating(true)
  }

  function submitCreate() {
    const name = draftName.trim()
    if (!name) return
    const expiresInDays = Number(draftExpiry)
    create.mutate(
      { name, expiresInDays },
      {
        onSuccess: ({ machine_token, token }) => {
          setCreating(false)
          setCopied(false)
          setMinted({ id: machine_token.id, name: machine_token.name, token, expiresAt: machine_token.expires_at })
          capture('machine_token_created', { expires_in_days: expiresInDays })
        },
      },
    )
  }

  async function copyToken(token: string) {
    try {
      await navigator.clipboard.writeText(token)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // A clipboard the browser will not give us is not a failure worth
      // reporting: the token is on screen and selectable.
    }
  }

  return (
    <AppShell>
      <PageHead
        index="04"
        eyebrow="Machine tokens"
        title="Credentials for machines"
        subtitle="A token lets a server, a container or a CI job run Archis as you, with nobody there to sign in. It is shown once, it expires, and each machine that uses it is a device on your account."
        actions={
          <Button size="sm" variant="primary" disabled={!canCreate} onClick={openCreate}>
            Create token
          </Button>
        }
      />

      <div className="stack" style={{ ['--gap' as string]: 'var(--s5)' }}>
        <div className="readout-grid">
          <div>
            <Readout value={live.length} caption="live tokens" tone={live.length > 0 ? 'signal' : undefined} />
          </div>
          <div>
            <Readout value={tokens?.length ?? 0} caption="ever created" />
          </div>
          <div>
            <Readout
              value={expiringSoon.length}
              caption="expire within 7 days"
              tone={expiringSoon.length > 0 ? 'amber' : undefined}
            />
          </div>
        </div>

        {account && !canCreate && (
          <Notice tone="warn">
            Your account has no active entitlement, so a token could not be used even if one
            were created. Every token you hold authenticates as you: when your access lapses,
            they all stop with it.
          </Notice>
        )}

        {isPending && <Loading label="Loading machine tokens" />}
        <ErrorNotice error={error} />
        <ErrorNotice error={revoke.error} />

        {minted && (
          <Panel title="Your new token" index="!" tone="signal">
            {/* ph-no-capture: the token is on this panel twice (the code and the export
                line). PostHog skips an element whose ancestor carries the class. */}
            <div
              className="stack ph-no-capture"
              style={{ ['--gap' as string]: 'var(--s4)' }}
              data-testid="minted-token"
            >
              <p style={{ margin: 0 }}>
                <strong>
                  Copy <em>{minted.name}</em> now — it is shown exactly once.
                </strong>{' '}
                It is stored hashed and cannot be recovered; if you lose it, revoke it and
                create another.
              </p>
              <div
                className="row ph-no-capture"
                style={{ gap: 'var(--s3)', alignItems: 'center', flexWrap: 'wrap' }}
              >
                <code
                  className="ph-no-capture"
                  data-testid="minted-token-secret"
                  style={{ wordBreak: 'break-all', userSelect: 'all', flex: '1 1 24ch' }}
                >
                  {minted.token}
                </code>
                <Button size="sm" className="ph-no-capture" onClick={() => copyToken(minted.token)}>
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              </div>
              <Notice tone="warn">
                This token is <strong>your</strong> credential, not a service account. Anyone
                holding it can do everything you can do with the CLI on this account — index
                your repositories, list and revoke your devices — until it expires on{' '}
                {formatDate(minted.expiresAt)} or you revoke it here. Treat it like a password.
              </Notice>
              <div className="term">
                <div className="term__bar">on a server: paste it once</div>
                <div className="term__body">
                  <div className="term__line">
                    <span className="term__prompt">$</span> chi auth login --with-token
                  </div>
                  <div className="term__line term__dim">
                    {'  '}# paste the token, or pipe it: chi auth login --with-token &lt; token.txt
                  </div>
                  <div className="term__line term__dim">
                    {'  '}the machine keeps it, and counts as one of your devices.
                  </div>
                </div>
              </div>
              <div className="term">
                <div className="term__bar">in a container: inject it, or mount it as a file</div>
                <div className="term__body">
                  <div className="term__line">
                    <span className="term__prompt">$</span> export CHI_TOKEN={minted.token}
                  </div>
                  <div className="term__line">
                    <span className="term__prompt">$</span> docker run -e CHI_TOKEN_FILE=/run/secrets/chi_token …
                  </div>
                  <div className="term__line term__dim">
                    {'  '}in CI, add CHI_EPHEMERAL=1 so builds do not each claim a device.
                  </div>
                </div>
              </div>
              <div className="row">
                <Button size="sm" variant="ghost" onClick={() => setMinted(null)}>
                  I have copied it
                </Button>
              </div>
            </div>
          </Panel>
        )}

        <Panel title="Tokens" index="01" padded={false}>
          {tokens && tokens.length === 0 && (
            <Empty>
              No machine tokens yet. Create one to run <code>chi</code> on a server, in a
              container or in a CI job without signing in.
            </Empty>
          )}

          {tokens && tokens.length > 0 && (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Token</th>
                    <th>Created</th>
                    <th>Expires</th>
                    <th>Last used</th>
                    <th>Device</th>
                    <th>Status</th>
                    <th className="actions" />
                  </tr>
                </thead>
                <tbody>
                  {tokens.map((token) => (
                    <tr key={token.id} data-testid={`machine-token-${token.name}`}>
                      <td>
                        <div className="stack" style={{ ['--gap' as string]: '2px' }}>
                          <span>{token.name}</span>
                          <Fig>{token.id}</Fig>
                        </div>
                      </td>
                      <td className="mono-note" title={formatDateTime(token.created_at)}>
                        {formatDate(token.created_at)}
                      </td>
                      <td className="mono-note" title={formatDateTime(token.expires_at)}>
                        {formatDate(token.expires_at)}
                        {isLive(token) && daysUntil(token.expires_at) <= 7 && (
                          <>
                            {' '}
                            <Badge tone="amber">soon</Badge>
                          </>
                        )}
                      </td>
                      <td className="mono-note" title={token.last_used_at ? formatDateTime(token.last_used_at) : undefined}>
                        {token.last_used_at ? formatRelative(token.last_used_at) : 'never'}
                      </td>
                      <td>
                        {token.device_id ? (
                          <Link to="/devices" className="mono-note" title={token.device_id}>
                            one shared device
                          </Link>
                        ) : (
                          <span className="mono-note">—</span>
                        )}
                      </td>
                      <td>
                        <TokenStatus token={token} />
                      </td>
                      <td className="actions">
                        {isLive(token) && (
                          <div className="row" style={{ justifyContent: 'flex-end' }}>
                            <Button size="sm" variant="danger" onClick={() => setConfirming(token)}>
                              Revoke
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        <Panel title="How machine tokens behave" index="02">
          <ul
            className="stack mono-note"
            style={{ ['--gap' as string]: 'var(--s2)', paddingLeft: 'var(--s5)', margin: 0 }}
          >
            <li>
              A token authenticates as you. It is not a seat and not a service account: when
              your subscription lapses, every token you hold stops with it.
            </li>
            <li>
              Creating tokens is free — they cost no device. Using one is not: each machine
              that authenticates with a token becomes a device on your account, with its own
              row and its own revoke, and counts against your device limit like a laptop.
            </li>
            <li>
              For CI, set <code>CHI_EPHEMERAL=1</code>. A build job keeps no state, so every
              run would otherwise claim a device it never gives back; with it, every job
              presenting one token shares a single row that is counted against nothing.
            </li>
            <li>
              Nothing rotates. <code>CHI_TOKEN</code> (or the file <code>CHI_TOKEN_FILE</code>{' '}
              names) is read on every start and written nowhere, so a container that loses its
              state directory has lost nothing. On a server,{' '}
              <code>chi auth login --with-token</code> keeps the token where the CLI keeps its
              credentials, and no variable has to be exported anywhere.
            </li>
            <li>
              Revoking stops the next cold start. A container already holding a lease keeps
              it until the grace window closes — up to 72 hours.
            </li>
            <li>
              Every token expires, at most a year out. Not because a leaked one is safer for
              it, but because the token nobody remembers issuing has to end somehow.
            </li>
            <li>
              A machine with a device of its own encrypts its index the ordinary way, with the
              keys its lease carries. A shared ephemeral row holds no encryption key, so an
              encrypted index there uses your own passphrase (<code>CHI_PASSPHRASE</code> or{' '}
              <code>CHI_PASSPHRASE_FILE</code>).
            </li>
          </ul>
        </Panel>
      </div>

      {creating && (
        <ConfirmDialog
          title="Create machine token"
          confirmLabel="Create token"
          pending={create.isPending}
          confirmDisabled={draftName.trim() === ''}
          onCancel={() => setCreating(false)}
          onConfirm={submitCreate}
          body={
            <>
              <Field
                label="Name"
                hint="Name it after where it will live — ci-build, staging-runner, my-devcontainer. It is the only handle you will have on a secret you cannot read back."
              >
                <input
                  className="input"
                  value={draftName}
                  onChange={(event) => setDraftName(event.target.value)}
                  maxLength={80}
                  placeholder="ci-build"
                  autoFocus
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault()
                      submitCreate()
                    }
                  }}
                />
              </Field>
              <Field label="Expires in" hint="A year is the ceiling. Shorter is safer for a secret in a CI config.">
                <Select
                  value={draftExpiry}
                  onChange={(event) => setDraftExpiry(event.target.value)}
                  options={[...EXPIRY_OPTIONS]}
                />
              </Field>
              <p className="mono-note" style={{ margin: 0 }}>
                The token will authenticate as <strong>{account?.email}</strong>, with
                everything that account can do.
              </p>
              <ErrorNotice error={create.error} />
            </>
          }
        />
      )}

      {confirming && (
        <ConfirmDialog
          title="Revoke machine token"
          danger
          confirmLabel="Revoke"
          pending={revoke.isPending}
          onCancel={() => setConfirming(null)}
          onConfirm={() =>
            revoke.mutate(confirming.id, {
              onSuccess: () => {
                setConfirming(null)
                if (minted?.id === confirming.id) setMinted(null)
                capture('machine_token_revoked')
              },
            })
          }
          body={
            <>
              <p style={{ margin: 0 }}>
                <strong>{confirming.name}</strong> stops working for every container, CI job
                and Codespace presenting it. Their next start is refused, naming this token.
              </p>
              <p className="mono-note" style={{ margin: 0 }}>
                A container already holding a lease keeps it for up to 72 hours. The device
                this token spoke through is revoked with it.
              </p>
            </>
          }
        />
      )}
    </AppShell>
  )
}

function TokenStatus({ token }: { token: MachineToken }) {
  if (token.revoked) return <Badge tone="coral">revoked</Badge>
  if (token.expired) return <Badge tone="amber">expired</Badge>
  return (
    <Badge tone="signal" dot>
      active
    </Badge>
  )
}

function isLive(token: MachineToken): boolean {
  return !token.revoked && !token.expired
}

function daysUntil(iso: string): number {
  const ms = new Date(iso).getTime() - Date.now()
  return Math.ceil(ms / (24 * 60 * 60 * 1000))
}
